import { Check, Clear } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import {
  useGetAllUserRolesQuery,
  AppRole,
  AlertSeverity,
  setAlert,
  useAppSelector,
  useGetRolesForUserQuery,
  useAppDispatch,
  useUpdateRolesForUserMutation,
  useGetUserPowerBiReportsQuery,
} from 'api';
import { TabPanelContainer } from 'app/common';
import { useState, useEffect } from 'react';

export const RolesTabPanel: React.FC<{ value: number; index: number }> = ({
  value,
  index,
}) => {
  const dispatch = useAppDispatch();
  const { selectedUser } = useAppSelector(state => state.userMaintenance);
  const { data: allUserRoles } = useGetAllUserRolesQuery();
  const { data: userRoles, refetch } = useGetRolesForUserQuery(
    selectedUser?.id
  );
  const { refetch: refetchReports } = useGetUserPowerBiReportsQuery(
    selectedUser?.id
  );
  const [updateAction, { isLoading: isUpdating, isSuccess, isError }] =
    useUpdateRolesForUserMutation();
  const [userRolesState, setUserRolesState] = useState<AppRole[]>();

  useEffect(() => {
    if (userRoles && allUserRoles && allUserRoles.length > 0) {
      const updateAllUserRoles = allUserRoles
        .map(ur => {
          const foundRole = userRoles.find(r => ur.id === r.id);
          if (foundRole) return { ...foundRole, active: true };
          return { ...ur, active: false };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      setUserRolesState(updateAllUserRoles);
    }
    // eslint-disable-next-line
  }, [userRoles, allUserRoles]);

  function handleCheckboxChange(
    e: React.ChangeEvent<HTMLInputElement>,
    role: AppRole
  ) {
    const updatedUserRoles = userRolesState?.map(ur =>
      ur.id === role.id ? { ...ur, active: !ur.active } : { ...ur }
    );
    setUserRolesState(updatedUserRoles);
  }

  async function handleSave() {
    const roleIds: number[] | undefined = userRolesState
      ?.map(ur => (ur.active ? ur.id : undefined))
      .filter((x): x is number => x !== undefined);

    if (roleIds && selectedUser) {
      await updateAction({ uid: selectedUser?.id, roleIds })
        .unwrap()
        .then(() => {
          dispatch(
            setAlert({
              message: 'Roles saved.',
              severity: AlertSeverity.Success,
            })
          );
          refetch();
          refetchReports();
        })
        .catch(() => {
          dispatch(
            setAlert({
              message: 'Something went wrong.',
              severity: AlertSeverity.Error,
            })
          );
        });
    }
  }

  const getStartIcon = () => {
    if (isUpdating)
      return <CircularProgress size={20} sx={{ color: grey[50] }} />;
    else if (isSuccess) return <Check sx={{ color: grey[50] }} />;
    else if (isError) return <Clear sx={{ color: grey[50] }} />;
  };

  return (
    <TabPanelContainer value={value} index={index}>
      <Box sx={{ columnCount: 5, minWidth: '800px' }}>
        <FormGroup>
          {userRolesState &&
            selectedUser &&
            userRolesState.map((role, index) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={role.active}
                    onChange={e => handleCheckboxChange(e, role)}
                  />
                }
                label={role.name}
                key={`${role.id}-${index}`}
              />
            ))}
        </FormGroup>
      </Box>

      <Box display="flex" justifyContent="center" paddingTop="5px">
        {userRolesState && selectedUser && (
          <Button
            variant="contained"
            size="large"
            sx={{ width: '300px', display: 'flex', alignItems: 'center' }}
            onClick={handleSave}
            startIcon={getStartIcon()}
          >
            <Typography>Save</Typography>
          </Button>
        )}
      </Box>
    </TabPanelContainer>
  );
};
